import React, { useState, useEffect } from 'react';
import { parseApproximateComponents, MONTH_NAMES } from '@/utils/taskLogic';
import { TIMING_YEAR_OPTIONS } from '@/utils/dateFormat';

// Structured "month position" control: primo/medio/ultimo + month (Danish agronomy convention -
// start/middle/end of month), as a peer of an exact date, a week number, a season, and free text
// rather than raw text. Produces/consumes the same canonical string understood by
// `parseApproximateComponents`/`getApproximateLabel` in `src/utils/taskLogic.js`:
//   "primo November 2026" / "medio November 2026" / "ultimo November 2026"
//
// Week-number entry used to live here as a second internal mode, duplicating the standalone
// "Week numbers" tab (`WeekNumberSelect.jsx`) that both modal files also expose at the top level.
// It's been removed to avoid that duplication - `WeekNumberSelect.jsx` is now the only place that
// produces the "week N Year" shape. `parseApproximateComponents` itself still recognizes that
// shape (unchanged, in `taskLogic.js`), so existing stored week values are unaffected; the modal
// files route them to the "Week numbers" tab on open instead of here (see `initialMode` /
// `initialTimeIntervalMode`), and their `switchMode` guards clear `interval` before it would ever
// reach this component in a shape it can't represent.

const POSITIONS = ['primo', 'medio', 'ultimo'];

// Default fallback when this mode is entered with no existing value yet - the year *options*
// list itself is the shared TIMING_YEAR_OPTIONS (imported above), not derived from this.
const currentYear = new Date().getFullYear();

const selectClass = 'rounded border px-2 py-1.5 bg-white dark:bg-gray-800 text-sm';

export default function ApproximateTimingSelect({ value, onChange }) {
  const initial = parseApproximateComponents(value);

  const [position, setPosition] = useState(initial?.position || 'primo');
  const [monthIndex, setMonthIndex] = useState(
    initial?.monthIndex !== undefined ? initial.monthIndex : new Date().getMonth()
  );
  const [year, setYear] = useState(initial?.year || String(currentYear));

  const emit = (next) => {
    if (next.position && next.monthIndex != null && next.year) {
      onChange(`${next.position} ${MONTH_NAMES[next.monthIndex]} ${next.year}`);
    }
  };

  // Re-sync local state if the incoming value changes from outside (e.g. switching between
  // diary entries being edited).
  useEffect(() => {
    const p = parseApproximateComponents(value);
    if (p?.mode === 'month') {
      setPosition(p.position);
      setMonthIndex(p.monthIndex);
      setYear(p.year);
    }
  }, [value]);

  // First time this mode is entered (no existing month-position value yet) - emit a sensible
  // default immediately rather than leaving the field blank.
  useEffect(() => {
    if (parseApproximateComponents(value)?.mode !== 'month') {
      emit({ position, monthIndex, year });
    }
    // Intentionally run once on mount only - subsequent syncs are handled by the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-wrap gap-2">
      <select
        value={position}
        onChange={(e) => {
          setPosition(e.target.value);
          emit({ position: e.target.value, monthIndex, year });
        }}
        className={selectClass}
      >
        {POSITIONS.map((p) => (
          <option key={p} value={p}>
            {p[0].toUpperCase() + p.slice(1)}
          </option>
        ))}
      </select>
      <select
        value={monthIndex}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          setMonthIndex(v);
          emit({ position, monthIndex: v, year });
        }}
        className={selectClass}
      >
        {MONTH_NAMES.map((m, i) => (
          <option key={m} value={i}>
            {m}
          </option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => {
          setYear(e.target.value);
          emit({ position, monthIndex, year: e.target.value });
        }}
        className={selectClass}
      >
        {TIMING_YEAR_OPTIONS.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
}
